import { getReferenceString, MedplumClient, parseReference } from "@medplum/core";
import { DiagnosticReport, Observation } from "@medplum/fhirtypes";

import { SNOMEDCodes } from "@/fhir/enums/snomed";
import { Systems } from "@/fhir/enums/systems";
import { getResourcesFromBundle, joinTokenString } from "@/utils/medplum";

import { getLabOrderInternalStatus, isTerminalLabOrderInternalStatus } from "../utils/labs";

/**
 * Returns observation IDs referenced by a diagnostic report, including panel members.
 *
 * @param medplum - Medplum client used to resolve panel member observations.
 * @param diagnosticReport - Diagnostic report whose result tree should be collected.
 */
async function collectDiagnosticReportObservationIds(
  medplum: MedplumClient,
  diagnosticReport: DiagnosticReport,
): Promise<Set<string>> {
  const observationIds = new Set<string>();
  const resultReferences = diagnosticReport.result ?? [];

  await Promise.all(
    resultReferences.map(async (resultReference) => {
      const [resourceType, observationId] = parseReference(resultReference) ?? [];
      if (resourceType !== "Observation" || !observationId) return;

      observationIds.add(observationId);

      const panelObservation = await medplum.readResource("Observation", observationId);
      for (const memberReference of panelObservation.hasMember ?? []) {
        const [memberType, memberId] = parseReference(memberReference) ?? [];
        if (memberType === "Observation" && memberId) {
          observationIds.add(memberId);
        }
      }
    }),
  );

  return observationIds;
}

/**
 * Excludes observations linked to terminal (cancelled/denied/revoked) lab orders.
 *
 * @param medplum - Medplum client.
 * @param observations - Candidate trend observations for a single patient and test code.
 */
export async function filterObservationsFromTerminalLabOrders(
  medplum: MedplumClient,
  observations: Observation[],
): Promise<Observation[]> {
  if (observations.length === 0) return observations;

  const subjectReference = observations[0]?.subject;
  if (!subjectReference) return observations;

  const laboratoryCategory = joinTokenString(Systems.SNOMED, SNOMEDCodes.LaboratoryProcedure);
  const { bundle } = await medplum.searchResources("ServiceRequest", {
    subject: getReferenceString(subjectReference),
    category: laboratoryCategory,
    _count: 1000,
  });

  const terminalLabOrders = getResourcesFromBundle("ServiceRequest", bundle).filter(
    (labOrder) =>
      isTerminalLabOrderInternalStatus(getLabOrderInternalStatus(labOrder)) ||
      labOrder.status === "revoked",
  );

  if (terminalLabOrders.length === 0) return observations;

  const excludedObservationIds = new Set<string>();

  await Promise.all(
    terminalLabOrders.map(async (terminalLabOrder) => {
      const diagnosticReport = await medplum.searchOne("DiagnosticReport", {
        "based-on": getReferenceString(terminalLabOrder),
        _sort: "-_lastUpdated",
      });
      if (!diagnosticReport) return;

      const reportObservationIds = await collectDiagnosticReportObservationIds(
        medplum,
        diagnosticReport,
      );
      reportObservationIds.forEach((observationId) => excludedObservationIds.add(observationId));
    }),
  );

  if (excludedObservationIds.size === 0) return observations;

  return observations.filter(
    (observation) => !observation.id || !excludedObservationIds.has(observation.id),
  );
}
